import { useState } from "react";
import { Button } from "../ui/Button";
import { api, ApiError, type DesignResponse } from "../../api/client";

const EXAMPLES: Array<{ label: string; text: string }> = [
  {
    label: "School attendance",
    text: "A survey about school attendance: how many days per week a child attends, whether transportation is a barrier, and any concerns the caregiver has about missed days.",
  },
  {
    label: "Housing stability",
    text: "A survey about housing stability: how many times the family has moved in the past year, whether they worry about losing housing, and what kind of support would help most.",
  },
];

/**
 * Paste/describe a survey, then POST it for a proposed schema (§12). This
 * component only proposes — it hands {template, notice} up to the parent
 * and never navigates or runs anything itself. The mandatory human review
 * step (SchemaReviewForm) is a SEPARATE component the parent renders next;
 * nothing here can skip straight to a runnable interview.
 */
export function SchemaComposer({ onGenerated }: { onGenerated: (result: DesignResponse) => void }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function generate() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.design(text);
      onGenerated(result);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not generate a schema. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <div>
        <h1 className="font-display text-xl text-ink">Design your own check-in</h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-soft">
          Paste a survey or describe a study, and Fieldnote will propose a short structured schema. You
          will review and edit every field before anything runs — nothing here can be run unreviewed.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {EXAMPLES.map((example) => (
          <Button
            key={example.label}
            type="button"
            variant="secondary"
            onClick={() => setText(example.text)}
            disabled={loading}
          >
            Example: {example.label}
          </Button>
        ))}
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste your survey text, or describe the study you want to run…"
        rows={10}
        disabled={loading}
        className="w-full flex-1 resize-none rounded-lg border border-border bg-paper p-3 text-sm text-ink placeholder:text-ink-soft focus:outline-none focus:ring-2 focus:ring-sage-strong"
        aria-label="Survey text or description"
      />

      {error && (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      )}

      <div>
        <Button type="button" onClick={generate} disabled={loading}>
          {loading ? "Generating…" : "Generate schema"}
        </Button>
      </div>
    </div>
  );
}
